import { useEffect, useState, useCallback } from "react";
import PageShell from "@/components/PageShell";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/ProfileDialog";
import EditUserDialog from "@/components/EditUserDialog";

export default function UsersPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "staff" });
  const [editing, setEditing] = useState(null);
  const { user: me, refresh } = useAuth();

  const load = useCallback(() => { api.get("/users").then((r) => setItems(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.email || !form.password || !form.name) return toast.error("Lengkapi semua kolom");
    try {
      await api.post("/users", form);
      toast.success("Pengguna dibuat"); setOpen(false);
      setForm({ email: "", password: "", name: "", role: "staff" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Hapus akun pengguna ini?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <PageShell title="Akun Pengguna">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-user-button"><Plus className="h-4 w-4 mr-1.5" />Tambah Pengguna</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Tambah Pengguna</DialogTitle><DialogDescription className="sr-only">Form akun pengguna baru</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
              <div className="space-y-1.5"><Label>Kata Sandi</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
              <div className="space-y-1.5"><Label>Peran</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="staff">Staf</SelectItem><SelectItem value="accounting">Accounting</SelectItem><SelectItem value="owner">Owner</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={submit} data-testid="user-submit-button">Simpan</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Card className="shadow-sm"><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Email</TableHead><TableHead>Peran</TableHead><TableHead className="w-24"></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium"><div className="flex items-center gap-3"><Avatar user={u} className="h-9 w-9 text-xs" /><span>{u.name}</span></div></TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell><Badge variant="outline" className={u.role === "staff" ? "bg-slate-100" : "bg-blue-50 text-blue-700 border-blue-200"}>{{ owner: "Owner", accounting: "Accounting" }[u.role] || "Staf"}</Badge></TableCell>
                <TableCell><div className="flex justify-end gap-1"><Button variant="ghost" size="icon" onClick={() => setEditing(u)} data-testid={`user-edit-${u.id}`}><Pencil className="h-4 w-4 text-blue-600" /></Button>{u.id !== me?.id && <Button variant="ghost" size="icon" onClick={() => del(u.id)} data-testid={`user-delete-${u.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>}</div></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
      <EditUserDialog target={editing} isSelf={editing?.id === me?.id} onClose={() => setEditing(null)}
        onSaved={() => { const self = editing?.id === me?.id; setEditing(null); load(); if (self) refresh(); }} />
    </PageShell>
  );
}
